import axios from "axios";
import { API } from "./api";

export const adminToken = () => localStorage.getItem("taven_admin_token");

// ---- ECOS session / tenant-context utilities (PR #48 corrective clusters 1 & 2) ----
export const ECOS_ORGANIZATION_KEY = "taven_ecos_organization";
export const ECOS_TENANT_KEY = "taven_ecos_tenant";
export const ECOS_TENANT_ORGANIZATION_KEY = "taven_ecos_tenant_organization";

const SESSION_INVALID_REASONS = new Set([
  "TOKEN_INVALID",
  "TOKEN_EXPIRED",
  "TOKEN_REVOKED",
  "SESSION_INVALID",
  "SESSION_EXPIRED",
  "SESSION_REVOKED",
]);

const tenantRequestControllers = new Set();

export const redirectAdminToLogin = () => {
  if (!window.location.pathname.startsWith("/admin/login")) {
    window.location.href = "/admin/login";
  }
};

export const adminSessionInvalidReason = (error) => {
  const data = error?.response?.data;
  const reason = data?.reason || data?.code || data?.error_code;
  return typeof reason === "string" ? reason.trim().toUpperCase() : "";
};

// A session may ONLY be revoked on a 401 or an explicit invalid/expired/revoked reason.
// Network errors, timeouts, and ordinary 403/429/500/502/503 responses preserve the session.
export const isDefinitiveAdminAuthenticationFailure = (error) =>
  error?.response?.status === 401 ||
  SESSION_INVALID_REASONS.has(adminSessionInvalidReason(error));

export const revokeAdminSession = () => {
  localStorage.removeItem("taven_admin_token");
  redirectAdminToLogin();
};

export const readPersistedEcosContext = () => {
  const organizationId = localStorage.getItem(ECOS_ORGANIZATION_KEY) || "";
  const tenantId = localStorage.getItem(ECOS_TENANT_KEY) || "";
  const tenantOrganizationId = localStorage.getItem(ECOS_TENANT_ORGANIZATION_KEY) || "";
  if (!organizationId || !tenantId || tenantOrganizationId !== organizationId) {
    localStorage.removeItem(ECOS_TENANT_KEY);
    localStorage.removeItem(ECOS_TENANT_ORGANIZATION_KEY);
    return { organizationId, tenantId: "" };
  }
  return { organizationId, tenantId };
};

export const persistEcosOrganization = (organizationId) => {
  if (organizationId) {
    localStorage.setItem(ECOS_ORGANIZATION_KEY, organizationId);
  } else {
    localStorage.removeItem(ECOS_ORGANIZATION_KEY);
  }
  // An organization transition atomically invalidates tenant context.
  localStorage.removeItem(ECOS_TENANT_KEY);
  localStorage.removeItem(ECOS_TENANT_ORGANIZATION_KEY);
};

export const persistEcosTenant = (tenantId, organizationId) => {
  if (tenantId && organizationId) {
    localStorage.setItem(ECOS_TENANT_KEY, tenantId);
    localStorage.setItem(ECOS_TENANT_ORGANIZATION_KEY, organizationId);
    return;
  }
  localStorage.removeItem(ECOS_TENANT_KEY);
  localStorage.removeItem(ECOS_TENANT_ORGANIZATION_KEY);
};

export const beginTenantAdminRequest = () => {
  const controller = new AbortController();
  tenantRequestControllers.add(controller);
  return {
    signal: controller.signal,
    finish: () => tenantRequestControllers.delete(controller),
  };
};

export const invalidateTenantAdminRequests = () => {
  tenantRequestControllers.forEach((controller) => controller.abort());
  tenantRequestControllers.clear();
};

export const isCanceledAdminRequest = (error) =>
  axios.isCancel(error) ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError";

export const adminClient = () => {
  const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${adminToken()}` } });
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isDefinitiveAdminAuthenticationFailure(error)) {
        revokeAdminSession();
      }
      return Promise.reject(error);
    }
  );
  return client;
};
